import { useState } from "react";
import { CalcCard, NumField, ResultRow, PrimaryButton, Section, SaveButton } from "@/components/CalcUI";
import { runCalc } from "@/lib/api";
import { toast } from "sonner";

export default function Cementing() {
  const [vol, setVol] = useState({ ann_cap: "0.0459", annulus_length: "3000", shoetrack_cap: "0.01443", shoetrack_length: "80" });
  const [volOut, setVolOut] = useState(null);

  const [sk, setSk] = useState({ slurry_bbl: "150", yield_ft3_per_sack: "1.15", water_gps_per_sack: "5.0" });
  const [skOut, setSkOut] = useState(null);

  const [disp, setDisp] = useState({ pipe_cap: "0.01776", well_depth: "10000", float_collar_depth: "9920", pump_output: "0.117" });
  const [dispOut, setDispOut] = useState(null);

  const [den, setDen] = useState({ cement_sg: "3.14", water_gps_per_sack: "5.0", additive_lb_per_sack: "0", additive_gal_per_sack: "0" });
  const [denOut, setDenOut] = useState(null);

  const run = async (calc, inp, setter) => {
    try {
      const r = await runCalc(calc, Object.fromEntries(Object.entries(inp).map(([k, v]) => [k, parseFloat(v)])));
      setter(r);
      if (r.error) toast.error(r.error);
    } catch {
      toast.error("Invalid inputs");
    }
  };

  return (
    <div data-testid="cementing-page">
      <Section
        title="Cementing Volumes"
        subtitle="Slurry, cement sacks, mix water, displacement, and slurry density — a full cementing job plan without a spreadsheet. Default Class G values shown; override for foam, latex, or lightweight blends."
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <CalcCard testid="slurry-vol-card" title="Slurry Volume" formula="V = C_ann·L_ann + C_pipe·L_shoetrack">
            <div className="grid grid-cols-2 gap-3">
              <NumField testid="cv-ann-cap" label="Annular capacity" unit="bbl/ft" value={vol.ann_cap} onChange={(v) => setVol({ ...vol, ann_cap: v })} />
              <NumField testid="cv-ann-len" label="Annulus length" unit="ft" value={vol.annulus_length} onChange={(v) => setVol({ ...vol, annulus_length: v })} />
              <NumField testid="cv-st-cap" label="Shoetrack pipe cap" unit="bbl/ft" value={vol.shoetrack_cap} onChange={(v) => setVol({ ...vol, shoetrack_cap: v })} />
              <NumField testid="cv-st-len" label="Shoetrack length" unit="ft" value={vol.shoetrack_length} onChange={(v) => setVol({ ...vol, shoetrack_length: v })} />
            </div>
            <div className="mt-4 flex justify-between">
              <PrimaryButton testid="cv-run" onClick={() => run("cement_slurry_volume", vol, setVolOut)}>Calculate</PrimaryButton>
              <SaveButton getPayload={() => volOut && { category: "Cementing", calculator: "cement_slurry_volume", inputs: vol, outputs: volOut, units: "field", default_name: "Slurry Volume" }} />
            </div>
            {volOut && (
              <div className="mt-4 border-t border-[#334155] pt-3">
                <ResultRow label="Annulus volume" value={volOut.annulus_volume_bbl} unit="bbl" />
                <ResultRow label="Shoetrack volume" value={volOut.shoetrack_volume_bbl} unit="bbl" />
                <ResultRow label="Total slurry" value={volOut.total_slurry_bbl} unit="bbl" emphasis />
                <ResultRow label="Total slurry" value={volOut.total_slurry_ft3} unit="ft³" />
              </div>
            )}
          </CalcCard>

          <CalcCard testid="sacks-card" title="Cement Sacks + Mix Water" formula="sk = V·5.6146/yield ; water = sk·gps">
            <div className="grid grid-cols-3 gap-3">
              <NumField testid="sk-vol" label="Slurry volume" unit="bbl" value={sk.slurry_bbl} onChange={(v) => setSk({ ...sk, slurry_bbl: v })} />
              <NumField testid="sk-yield" label="Slurry yield" unit="ft³/sk" value={sk.yield_ft3_per_sack} onChange={(v) => setSk({ ...sk, yield_ft3_per_sack: v })} hint="Class G ~ 1.15" />
              <NumField testid="sk-water" label="Water" unit="gal/sk" value={sk.water_gps_per_sack} onChange={(v) => setSk({ ...sk, water_gps_per_sack: v })} hint="Class G ~ 5.0" />
            </div>
            <div className="mt-4 flex justify-between">
              <PrimaryButton testid="sk-run" onClick={() => run("cement_sacks", sk, setSkOut)}>Calculate</PrimaryButton>
              <SaveButton getPayload={() => skOut && { category: "Cementing", calculator: "cement_sacks", inputs: sk, outputs: skOut, units: "field", default_name: "Cement Sacks" }} />
            </div>
            {skOut && (
              <div className="mt-4 border-t border-[#334155] pt-3">
                <ResultRow label="Sacks of cement" value={skOut.sacks_of_cement?.toLocaleString()} unit="sk" emphasis />
                <ResultRow label="Mix water" value={skOut.mix_water_gal?.toLocaleString()} unit="gal" />
                <ResultRow label="Mix water" value={skOut.mix_water_bbl} unit="bbl" />
              </div>
            )}
          </CalcCard>

          <CalcCard testid="disp-card" title="Displacement Volume" formula="V = C_pipe·(D_well − D_fc)">
            <div className="grid grid-cols-2 gap-3">
              <NumField testid="d-cap" label="Pipe capacity" unit="bbl/ft" value={disp.pipe_cap} onChange={(v) => setDisp({ ...disp, pipe_cap: v })} />
              <NumField testid="d-well" label="Well depth" unit="ft" value={disp.well_depth} onChange={(v) => setDisp({ ...disp, well_depth: v })} />
              <NumField testid="d-fc" label="Float collar depth" unit="ft" value={disp.float_collar_depth} onChange={(v) => setDisp({ ...disp, float_collar_depth: v })} />
              <NumField testid="d-pump" label="Pump output" unit="bbl/stk" value={disp.pump_output} onChange={(v) => setDisp({ ...disp, pump_output: v })} />
            </div>
            <div className="mt-4 flex justify-between">
              <PrimaryButton testid="d-run" onClick={() => run("cement_displacement", disp, setDispOut)}>Calculate</PrimaryButton>
              <SaveButton getPayload={() => dispOut && { category: "Cementing", calculator: "cement_displacement", inputs: disp, outputs: dispOut, units: "field", default_name: "Displacement" }} />
            </div>
            {dispOut && !dispOut.error && (
              <div className="mt-4 border-t border-[#334155] pt-3">
                <ResultRow label="Displacement volume" value={dispOut.displacement_bbl} unit="bbl" emphasis />
                <ResultRow label="Strokes" value={dispOut.strokes} unit="stk" />
              </div>
            )}
          </CalcCard>

          <CalcCard testid="den-card" title="Slurry Density" formula="ρ = W_sk / V_sk">
            <div className="grid grid-cols-2 gap-3">
              <NumField testid="dn-sg" label="Cement SG" unit="—" value={den.cement_sg} onChange={(v) => setDen({ ...den, cement_sg: v })} hint="Class G ≈ 3.14" />
              <NumField testid="dn-water" label="Mix water" unit="gal/sk" value={den.water_gps_per_sack} onChange={(v) => setDen({ ...den, water_gps_per_sack: v })} />
              <NumField testid="dn-adlb" label="Additive weight" unit="lb/sk" value={den.additive_lb_per_sack} onChange={(v) => setDen({ ...den, additive_lb_per_sack: v })} />
              <NumField testid="dn-adgal" label="Additive volume" unit="gal/sk" value={den.additive_gal_per_sack} onChange={(v) => setDen({ ...den, additive_gal_per_sack: v })} />
            </div>
            <div className="mt-4 flex justify-between">
              <PrimaryButton testid="dn-run" onClick={() => run("cement_slurry_density", den, setDenOut)}>Calculate</PrimaryButton>
              <SaveButton getPayload={() => denOut && !denOut.error && { category: "Cementing", calculator: "cement_slurry_density", inputs: den, outputs: denOut, units: "field", default_name: "Slurry Density" }} />
            </div>
            {denOut && !denOut.error && (
              <div className="mt-4 border-t border-[#334155] pt-3">
                <ResultRow label="Slurry density" value={denOut.slurry_density_ppg} unit="ppg" emphasis />
                <ResultRow label="Slurry yield" value={denOut.slurry_yield_ft3_per_sack} unit="ft³/sk" />
                <ResultRow label="Weight per sack" value={denOut.weight_per_sack_lb} unit="lb" />
              </div>
            )}
          </CalcCard>
        </div>
      </Section>
    </div>
  );
}
